import { computed, ref, type ComputedRef } from 'vue'
import { useHubStore, type IHub } from '@/stores/hubs.store'
import { useBaseStore } from '@/stores/base.store'
import { storeToRefs } from 'pinia'

export interface ModuleSummary {
	_id: string
	serial: string
	name?: string
	customName?: string
	state?: string
	lastLog?: string | number | null
	alertInterval?: number | null
	show?: boolean
}

export interface GraphSummary {
	_id: string
	name?: string
	state?: string
	lastLog?: string | number | null
	type?: string
	moduleId?: string | null
	projectId?: string | null
	locationId?: string | null
}

export type SortOptionValue =
	| 'lastLog_desc'
	| 'lastLog_asc'
	| 'name_asc'
	| 'name_desc'
	| 'projectName_asc'
	| 'projectName_desc'
	| 'moduleCount_desc'
	| 'moduleCount_asc'
	| 'state_asc'

export interface SortOption {
	value: SortOptionValue
	label: string
}

export const sortOptions: SortOption[] = [
	{ value: 'lastLog_desc', label: 'Last Call (newest)' },
	{ value: 'lastLog_asc', label: 'Last Call (oldest)' },
	{ value: 'name_asc', label: 'Hub Name (A→Z)' },
	{ value: 'name_desc', label: 'Hub Name (Z→A)' },
	{ value: 'projectName_asc', label: 'Project (A→Z)' },
	{ value: 'projectName_desc', label: 'Project (Z→A)' },
	{ value: 'moduleCount_desc', label: 'Module Count (high→low)' },
	{ value: 'moduleCount_asc', label: 'Module Count (low→high)' },
	{ value: 'state_asc', label: 'State (A→Z)' },
]

interface HubsState {
	search: ReturnType<typeof ref<string>>
	selectedSort: ReturnType<typeof ref<SortOptionValue>>
	fetchError: ReturnType<typeof ref<string | null>>
	sortOptions: SortOption[]
	isFetching: ComputedRef<boolean>
	filteredHubs: ComputedRef<IHub[]>
	totalModules: ComputedRef<number>
	filteredCount: ComputedRef<number>
	stateBadgeClass: (state: string) => string
	projectNameBySerial: ComputedRef<Record<string, string>>
	locationNameBySerial: ComputedRef<Record<string, string>>
	modulesBySerial: ComputedRef<Record<string, ModuleSummary[]>>
	graphsBySerial: ComputedRef<Record<string, GraphSummary[]>>
	moduleCountBySerial: ComputedRef<Record<string, number>>
	graphCountBySerial: ComputedRef<Record<string, number>>
}

let hubsState: HubsState | null = null

function createHubsState(): HubsState {
	const hubStore = useHubStore()
	const baseStore = useBaseStore()

	const { hubs } = storeToRefs(hubStore)
	const { megaMap } = storeToRefs(baseStore)

	const search = ref('')
	const selectedSort = ref<SortOptionValue>(sortOptions[0].value)
	const fetchError = ref<string | null>(null)

	const isFetching = computed(
		() => hubStore.loadingMap['listAllHubs'] || baseStore.loadingMap['fetchMegalist'],
	)

	const assignmentBySerial = computed(() => {
		const result: Record<
			string,
			{
				projectId: string
				projectName: string
				locationId: string
				locationName: string
				modules: ModuleSummary[]
				graphs: GraphSummary[]
			}
		> = {}

		const projects = megaMap.value?.projects || {}
		for (const [projectId, project] of Object.entries<any>(projects)) {
			const projectName = project.meta?.name || project.meta?.projectNumber || '—'
			for (const [locationId, location] of Object.entries<any>(project.locations || {})) {
				const locationName = location.meta?.name || '—'
				for (const [serial, hub] of Object.entries<any>(location.hubs || {})) {
					result[serial] = {
						projectId,
						projectName,
						locationId,
						locationName,
						modules: Object.values(hub.modules || {}) as ModuleSummary[],
						graphs: Object.values(hub.graphs || {}) as GraphSummary[],
					}
				}
			}
		}
		return result
	})

	const projectNameBySerial = computed<Record<string, string>>(() => {
		const map: Record<string, string> = {}
		for (const [serial, info] of Object.entries(assignmentBySerial.value)) {
			map[serial] = info.projectName
		}
		return map
	})

	const locationNameBySerial = computed<Record<string, string>>(() => {
		const map: Record<string, string> = {}
		for (const [serial, info] of Object.entries(assignmentBySerial.value)) {
			map[serial] = info.locationName
		}
		return map
	})

	const modulesBySerial = computed<Record<string, ModuleSummary[]>>(() => {
		const map: Record<string, ModuleSummary[]> = {}
		for (const [serial, info] of Object.entries(assignmentBySerial.value)) {
			map[serial] = info.modules || []
		}
		return map
	})

	const graphsBySerial = computed<Record<string, GraphSummary[]>>(() => {
		const map: Record<string, GraphSummary[]> = {}
		for (const [serial, info] of Object.entries(assignmentBySerial.value)) {
			map[serial] = info.graphs || []
		}
		return map
	})

	const moduleCountBySerial = computed<Record<string, number>>(() => {
		const map: Record<string, number> = {}
		for (const [serial, list] of Object.entries(modulesBySerial.value)) {
			map[serial] = list.length
		}
		return map
	})

	const graphCountBySerial = computed<Record<string, number>>(() => {
		const map: Record<string, number> = {}
		for (const [serial, list] of Object.entries(graphsBySerial.value)) {
			map[serial] = list.length
		}
		return map
	})

	const sortedHubs = computed<IHub[]>(() => {
		const list = hubs.value.slice()
		const [sortKey = '', sortDir = 'asc'] = selectedSort.value.split('_')
		if (!sortKey) return list
		const direction = sortDir === 'desc' ? -1 : 1
		return list.sort((a, b) => {
			const aValue = getSortValue(a, sortKey, {
				projectNameBySerial: projectNameBySerial.value,
				locationNameBySerial: locationNameBySerial.value,
				moduleCountBySerial: moduleCountBySerial.value,
				graphCountBySerial: graphCountBySerial.value,
			})
			const bValue = getSortValue(b, sortKey, {
				projectNameBySerial: projectNameBySerial.value,
				locationNameBySerial: locationNameBySerial.value,
				moduleCountBySerial: moduleCountBySerial.value,
				graphCountBySerial: graphCountBySerial.value,
			})
			if (aValue === bValue) return 0
			if (typeof aValue === 'number' && typeof bValue === 'number') {
				return direction * (aValue < bValue ? -1 : 1)
			}
			return direction * String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' })
		})
	})

	const filteredHubs = computed<IHub[]>(() => {
		const query = search.value.trim().toLowerCase()
		if (!query) return sortedHubs.value
		return sortedHubs.value.filter((hub) => {
			const values = [
				hub.name,
				hub.serial,
				hub.state,
				hub.locationId,
				hub.currentSet?.network?.ipAddress,
				projectNameBySerial.value[hub.serial],
				locationNameBySerial.value[hub.serial],
			]
			return values.some((value) => value && String(value).toLowerCase().includes(query))
		})
	})

	const totalModules = computed(() =>
		filteredHubs.value.reduce((sum, hub) => sum + (moduleCountBySerial.value[hub.serial] || 0), 0),
	)

	const filteredCount = computed(() => filteredHubs.value.length)

	const stateBadgeClass = (state: string) => {
		const normalized = (state || '').toLowerCase()
		if (normalized === 'active' || normalized === 'ok') return 'badge-success badge-soft'
		if (normalized === 'warning' || normalized === 'warn') return 'badge-warning badge-soft'
		if (normalized === 'alert' || normalized === 'error') return 'badge-error badge-soft'
		if (normalized === 'deactivated' || normalized === 'inactive') return 'badge-neutral badge-outline'
		return 'badge-ghost'
	}

	return {
		search,
		selectedSort,
		sortOptions,
		fetchError,
		isFetching,
		filteredHubs,
		totalModules,
		filteredCount,
		stateBadgeClass,
		projectNameBySerial,
		locationNameBySerial,
		modulesBySerial,
		graphsBySerial,
		moduleCountBySerial,
		graphCountBySerial,
	}
}

export const useHubs = (): HubsState => {
	if (!hubsState) hubsState = createHubsState()
	return hubsState
}

const normalizeTimestamp = (value: string | number | null | undefined): number => {
	if (typeof value === 'number' && !Number.isNaN(value)) return value
	if (typeof value === 'string') {
		const ms = Date.parse(value)
		if (!Number.isNaN(ms)) return ms
	}
	return Number.NEGATIVE_INFINITY
}

function getSortValue(
	hub: IHub,
	key: string,
	maps: {
		projectNameBySerial: Record<string, string>
		locationNameBySerial: Record<string, string>
		moduleCountBySerial: Record<string, number>
		graphCountBySerial: Record<string, number>
	},
): string | number {
	switch (key) {
		case 'lastLog':
			return normalizeTimestamp(hub.lastLog)
		case 'name':
			return (hub.name || hub.serial || '').toLowerCase()
		case 'projectName':
			return (maps.projectNameBySerial[hub.serial] || '').toLowerCase()
		case 'locationName':
			return (maps.locationNameBySerial[hub.serial] || '').toLowerCase()
		case 'moduleCount':
			return maps.moduleCountBySerial[hub.serial] || 0
		case 'state':
			return (hub.state || '').toLowerCase()
		default:
			return ''
	}
}
